import { Link } from "react-router-dom";
import { 
  Mail, 
  Calendar,
  Sparkles,
  ArrowUpRight
} from "lucide-react";
import { motion } from "framer-motion";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden pt-20 pb-10 bg-black">
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>
      
      {/* Gold accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gold to-transparent"></div>
      
      {/* Floating orbs for ambiance */}
      <motion.div
        className="absolute top-10 left-10 w-64 h-64 bg-gold/10 rounded-full blur-3xl"
        animate={{
          x: [0, 30, 0],
          y: [0, -20, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
          {/* Column 1: Company description */}
          <div className="space-y-6">
            <motion.a 
              href="https://www.optigoldpmo.com" 
              title="OptiGold PMO Home"
              className="inline-block group"
              whileHover={{ scale: 1.05 }}
            >
              <div className="relative">
                <div className="absolute -inset-2 bg-gold/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <img 
                  src="/optigold-logo-color.svg" 
                  alt="OptiGold Logo" 
                  className="h-16 relative z-10 filter brightness-110" 
                />
              </div>
            </motion.a>
            <p className="text-gray-400 leading-relaxed">
              Partnering with organizations to solve their most pressing project, program, portfolio, and PMO pain points.
            </p>
          </div>

          {/* Column 2: Services */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400">
              Services
            </h3>
            <ul className="space-y-3">
              {[
                { path: '/services/quick-wins', label: 'Quick Wins & Targeted Solutions' },
                { path: '/services/fractional-pmo', label: 'Fractional PMO Leadership' },
                { path: '/services/pmo-transformation', label: 'PMO Transformation' },
                { path: '/services/project-leadership-coaching', label: 'Project Leadership Coaching' },
              ].map((item) => (
                <motion.li key={item.path} whileHover={{ x: 5 }}>
                  <Link 
                    to={item.path} 
                    className="text-gray-400 hover:text-gold transition-colors flex items-center gap-1 group"
                  >
                    {item.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </motion.li>
              ))}
            </ul>
          </div>
          
          {/* Column 3: Company */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400">
              Company
            </h3>
            <ul className="space-y-3">
              {[
                { path: '/about', label: 'About Us' },
                { path: '/assessment', label: 'Free Assessment' },
                { path: '/contact', label: 'Contact' },
                { href: 'https://blog.optigoldpmo.com', label: 'Visit Our Blog', external: true },
              ].map((item) => (
                <motion.li key={item.path || item.href} whileHover={{ x: 5 }}>
                  {item.external ? (
                    <a 
                      href={item.href} 
                      className="text-gray-400 hover:text-gold transition-colors flex items-center gap-1 group"
                    >
                      {item.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  ) : (
                    <Link 
                      to={item.path} 
                      className="text-gray-400 hover:text-gold transition-colors flex items-center gap-1 group"
                    >
                      {item.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  )}
                </motion.li>
              ))}
            </ul>
          </div>
          
          {/* Column 4: Get In Touch */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold to-amber-400">
              Get In Touch
            </h3>
            <ul className="space-y-4">
              <motion.li whileHover={{ x: 5 }}>
                <a 
                  href="mailto:inquiries@optigold.com" 
                  className="flex items-center gap-3 text-gray-400 hover:text-gold transition-colors group"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-gold/20 to-amber-400/20 rounded-lg flex items-center justify-center border border-gold/30 group-hover:border-gold transition-all">
                    <Mail size={16} className="text-gold" />
                  </div>
                  inquiries@optigold.com
                </a>
              </motion.li>
              
              <motion.li whileHover={{ x: 5 }}>
                <a 
                  href="https://cal.com/noah.mccloud/optigolddiscoverycall" 
                  className="flex items-center gap-3 text-gray-400 hover:text-gold transition-colors group"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-gold/20 to-amber-400/20 rounded-lg flex items-center justify-center border border-gold/30 group-hover:border-gold transition-all">
                    <Calendar size={16} className="text-gold" />
                  </div>
                  Schedule a Discovery Call
                </a>
              </motion.li>
              
              <motion.li whileHover={{ x: 5 }}>
                <a 
                  href="https://www.linkedin.com/company/optigold/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-400 hover:text-gold transition-colors group"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-gold/20 to-amber-400/20 rounded-lg flex items-center justify-center border border-gold/30 group-hover:border-gold transition-all">
                    <img 
                      src="/linkedin-official.svg" 
                      alt="LinkedIn" 
                      className="w-4 h-4 filter brightness-0 invert" 
                    />
                  </div>
                  Connect With Us
                </a>
              </motion.li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-16 pt-8 border-t border-gold/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              &trade; {currentYear} OptiGold PMO Consulting. All rights reserved.
            </p>
            
            <div className="flex items-center gap-6">
              <Link 
                to="/privacy" 
                className="text-gray-500 hover:text-gold text-sm transition-colors"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-gray-500 hover:text-gold text-sm transition-colors"
              >
                Terms & Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
